import { FC, useCallback, useEffect, useState } from 'react'
import {
  PlaidLinkError,
  PlaidLinkOnEvent,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExit,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOptions,
  usePlaidLink,
} from 'react-plaid-link'
import { useNavigate } from 'react-router'
import { includesCurrentAccount, submitAccountDataAndContinueToPayment } from 'services'
import { Box, Button, Text } from '@mrshmllw/smores-react'

import { resumeTokenToHash } from './resumeToken'
import { ErrorCode } from '../../../../Error/OpenBankingErrorPage'

import env from 'env'
import { useGetLinkTokenQuery } from 'app/services'
import { logger, useEventTracking, useFeatureFlags, useQuery } from 'utils'

type PlaidProps = {
  oauthNonce: string
  oauthStateId?: string
  setIsLoading: (arg: boolean) => void
  callToAction: string
  updateMode: boolean
  postOAuth: boolean
  openAutomatically?: boolean
  quoteId: string
  resumeToken: string
}

const ACCESS_TOKEN_KEY = 'plaid_public_access_token'

export const getCurrentPlaidToken = () => {
  const currentPlaidToken = window.sessionStorage.getItem(ACCESS_TOKEN_KEY)
  if (!currentPlaidToken) {
    return null
  }

  return currentPlaidToken
}

export const setCurrentPlaidToken = (token: string): void => {
  window.sessionStorage.setItem(ACCESS_TOKEN_KEY, token)
}

const CONNECTIONS_KEY = 'plaid_successful_connections'

type Connection = {
  timestamp: Date
  type: 'NEW' | 'UPDATE'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isConnection = (json: any): json is Connection => {
  return !!json.timestamp && (json.type === 'NEW' || json.type === 'UPDATE')
}

const getBankConnections = (): Connection[] => {
  const connectionsJson = window.sessionStorage.getItem(CONNECTIONS_KEY)
  const connections = connectionsJson ? JSON.parse(connectionsJson) : {}

  if (connections.connections) {
    return connections.connections
      .map((connection: { timestamp: string | number | Date; type: string | undefined }) => {
        if (connection.timestamp) {
          connection.timestamp = new Date(connection.timestamp)
        }

        return isConnection(connection) ? connection : undefined
      })
      .filter((connection: Connection | undefined) => !!connection)
  }

  return []
}

const getLastSuccessfulNewConnectionTimestamp = (): Date | undefined => {
  const connections = getBankConnections().filter((connection) => connection.type === 'NEW')

  return connections.length === 0 ? undefined : connections[connections.length - 1].timestamp
}

export const getLastSuccessfulConnectionTimestamp = (): Date | undefined => {
  const connections = getBankConnections()

  return connections.length === 0 ? undefined : connections[connections.length - 1].timestamp
}

export const storeSuccessfulBankConnection = (updateMode: boolean): void => {
  const connection: Connection = {
    timestamp: new Date(),
    type: updateMode ? 'UPDATE' : 'NEW',
  }

  const connections = getBankConnections()

  connections.push(connection)

  window.sessionStorage.setItem(CONNECTIONS_KEY, JSON.stringify({ connections: connections }))
}

export const OpenPlaidButton: FC<PlaidProps> = ({
  oauthNonce,
  setIsLoading,
  callToAction,
  updateMode,
  openAutomatically,
  postOAuth,
  oauthStateId,
  quoteId,
  resumeToken,
}) => {
  const query = useQuery()
  const navigate = useNavigate()
  const track = useEventTracking()
  const {
    postQuoteFeatureFlags: { growth_enable_link_token },
  } = useFeatureFlags()

  const { data: linkTokenData, error: getLinkTokenError } = useGetLinkTokenQuery(quoteId)
  const [paymentPlan, setPaymentPlan] = useState('')
  const [shouldOpenPlaid, setShouldOpenPlaid] = useState(false)

  const redirectWithPlaidErrorCode = useCallback(
    (plaidErrorCode: string | null) => {
      navigate(
        `/quote/${quoteId}/connect-your-bank/error?payment=${paymentPlan}&plaidErrorCode=${plaidErrorCode}`,
      )
    },
    [quoteId, navigate, paymentPlan],
  )

  const redirectWithErrorCode = useCallback(
    (errorCode: ErrorCode) => {
      if (errorCode !== 'connection_expired') {
        navigate(
          `/quote/${quoteId}/connect-your-bank/error?payment=${paymentPlan}&errorCode=${errorCode}`,
        )
      }
    },
    [quoteId, navigate, paymentPlan],
  )

  const onSuccess: PlaidLinkOnSuccess = useCallback(
    (token, metadata) => {
      setIsLoading(true)

      const currentToken = getCurrentPlaidToken()

      if (updateMode && currentToken) {
        navigate(`/quote/${quoteId}/payment/${paymentPlan}`)
        storeSuccessfulBankConnection(updateMode)
        return
      }

      const hasCurrentAccount = includesCurrentAccount(metadata.accounts)

      if (!hasCurrentAccount) {
        navigate(
          `/quote/${quoteId}/connect-your-bank/error?payment=${paymentPlan}&errorCode=wrong_account_metadata`,
        )
        return
      }

      if (hasCurrentAccount && paymentPlan && quoteId) {
        submitAccountDataAndContinueToPayment(
          navigate,
          quoteId,
          paymentPlan,
          token,
          redirectWithErrorCode,
        )
      }
    },
    [quoteId, navigate, setIsLoading, paymentPlan, updateMode, redirectWithErrorCode],
  )

  const handleError = useCallback(
    (
      error: PlaidLinkError,
      metadata?: PlaidLinkOnEventMetadata | PlaidLinkOnSuccessMetadata | PlaidLinkOnExitMetadata,
    ) => {
      const errorCode = error.error_code
      const plaidToken = getCurrentPlaidToken()

      // This happens when after connecting to the same account in rapid succession in update mode.
      // Plaid displays a message saying that the account is already connected,
      // forcing the user to manually exit the flow,
      // which results in an "error" with this code.
      if (errorCode === 'item-no-error' && !!plaidToken) {
        onSuccess(plaidToken, metadata as unknown as PlaidLinkOnSuccessMetadata)
        return
      }

      redirectWithPlaidErrorCode(errorCode)
    },
    [redirectWithPlaidErrorCode, onSuccess],
  )

  const onExit: PlaidLinkOnExit = useCallback(
    (error, metadata) => {
      if (error) {
        handleError(error, metadata)
        return
      }

      const currentTimestampMs = new Date().getTime()

      const lastNewConnectionTimestampMs =
        getLastSuccessfulNewConnectionTimestamp()?.getTime() ?? currentTimestampMs
      const tokenValidForMs = 30 * 60 * 1000
      const connectionExpiryTimestampMs = new Date(
        lastNewConnectionTimestampMs + tokenValidForMs,
      ).getTime()

      if (currentTimestampMs >= connectionExpiryTimestampMs) {
        redirectWithErrorCode('connection_expired')

        return
      }

      if (metadata.status !== 'connected') {
        redirectWithPlaidErrorCode(metadata.status)
      }
    },
    [handleError, redirectWithErrorCode, redirectWithPlaidErrorCode],
  )

  const onEvent: PlaidLinkOnEvent = useCallback(
    (eventName, metadata) => {
      logger.info(`Log Plaid ${postOAuth ? 'Post' : 'Pre'}-redirect Metadata`, {
        eventName,
        metadata,
        quoteId,
      })
    },
    [quoteId, postOAuth],
  )

  const currentToken = getCurrentPlaidToken()

  const getRedirectUri = useCallback(() => {
    return `${
      env.appUrl
    }/car-insurance-quote/open-banking-authentication?quoteId=${quoteId}&payment=${paymentPlan}&updateMode=${updateMode}${resumeTokenToHash(
      resumeToken,
    )}`
  }, [quoteId, paymentPlan, updateMode, resumeToken])

  const config: PlaidLinkOptions = {
    countryCodes: ['GB'],
    clientName: 'Marshmallow',
    env: env.plaidEnv,
    oauthNonce,
    oauthStateId: postOAuth && !!oauthStateId ? oauthStateId : undefined,
    oauthRedirectUri: postOAuth ? undefined : getRedirectUri(),
    onExit,
    onSuccess,
    onLoad: openAutomatically ? () => setShouldOpenPlaid(true) : undefined,
    onEvent,
    product: ['transactions', 'identity'],
    webhook: postOAuth ? undefined : env.plaidWebhook,
    token: currentToken && updateMode ? currentToken : (linkTokenData?.linkToken ?? null),
  }

  const publicKeyConfig: PlaidLinkOptions = {
    countryCodes: ['GB'],
    clientName: 'Marshmallow',
    env: env.plaidEnv,
    oauthNonce,
    oauthStateId: postOAuth && !!oauthStateId ? oauthStateId : undefined,
    oauthRedirectUri: postOAuth ? undefined : getRedirectUri(),
    onExit,
    onSuccess,
    onLoad: openAutomatically ? () => setShouldOpenPlaid(true) : undefined,
    onEvent,
    product: ['transactions', 'identity'],
    publicKey: env.plaidPublicKey,
    webhook: postOAuth ? undefined : env.plaidWebhook,
    token: currentToken && updateMode ? currentToken : undefined,
  }

  const { open, ready, error } = usePlaidLink(growth_enable_link_token ? config : publicKeyConfig)

  const handleOpenPlaid = () => {
    setShouldOpenPlaid(true)
    track('Connect your bank page - Clicked Verify me button')
  }

  useEffect(() => {
    const paymentPlan = query.get('payment')

    if (paymentPlan) {
      setPaymentPlan(paymentPlan)
    }
  }, [query])

  useEffect(() => {
    if (error && postOAuth) {
      handleError(error as unknown as PlaidLinkError)
    }

    if (getLinkTokenError && growth_enable_link_token) {
      redirectWithErrorCode('unable_to_create_link_token')
    }
  }, [error, quoteId, paymentPlan, postOAuth, handleError, getLinkTokenError])

  useEffect(() => {
    if (shouldOpenPlaid) {
      open()
    }
  }, [shouldOpenPlaid, open])

  return (
    <Box mb="16px" width="100%">
      <Button
        id="openPlaidIntegration"
        handleClick={handleOpenPlaid}
        primary
        disabled={!ready}
        forcedWidth="100%"
      >
        {callToAction}
      </Button>

      {error && (
        <Box mt="8px" px="16px">
          <Text color="strawberry" typo="body-small" tag="p">
            Plaid has not loaded, please reload the page and try again
          </Text>
        </Box>
      )}
    </Box>
  )
}
